// SeasonalPricingEditor.jsx
import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";
import { CalendarIcon } from "lucide-react";
import Required from "../../utils/Required";
import { fromISODate, toISODate } from "../../utils/datetime";
/* ─────────────────────────────────── helpers */
const seasonErr = (errors, idx, field) => {
  const entry = Object.entries(errors).find(([k]) =>
    k.includes(`season${idx}.${field}`)
  );
  return entry ? entry[1] : null;
};

export const MMddInput = forwardRef(
  (
    {
      value = "",
      onClick,
      onBlur,
      placeholder,
      className,
      onDateSelect,
    },
    ref
  ) => {
    const isoToMMdd = (iso) => {
      return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.slice(5) : '';
    };
   const toDisplay = (v) => {
     if (!v) return "";
     if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return isoToMMdd(v); // ISO from DB
     if (/^\d{2}-\d{2}$/.test(v))      return v;             // “MM-DD” from DatePicker
     return "";
   };

   const [localVal, setLocalVal] = useState(() => toDisplay(value));

   useEffect(() => {
     setLocalVal(toDisplay(value));
   }, [value]);

    useEffect(() => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        setLocalVal(isoToMMdd(value));
      }
    }, [value]);

    const handleChange = (e) => {
      const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 4);

      let monthPart = digits.slice(0, 2);
      let dayPart = digits.slice(2);

      // ----- \n      // Auto‑correct MONTH as soon as two digits are available
      // 31 → 12, 00 → 01, etc.
      // -----
      if (monthPart.length === 2) {
        let mm = parseInt(monthPart, 10);
        mm = Math.min(Math.max(mm, 1), 12);
        monthPart = String(mm).padStart(2, "0");
      }

      // Build the string that the user sees while typing
      let formatted = monthPart;
      if (digits.length > 2) {
        formatted += `-${dayPart}`;
      }
      setLocalVal(formatted);

      // ----- \n      // When 4 digits are present we can validate & emit the ISO date
      // -----
      if (digits.length === 4) {
        const year = new Date().getFullYear();
        const mmNum = parseInt(monthPart, 10);
        let ddNum = parseInt(dayPart, 10);

        const maxDays = daysInMonth(mmNum, year);
        ddNum = Math.min(Math.max(ddNum, 1), maxDays);

        const mmStr = String(mmNum).padStart(2, "0");
        const ddStr = String(ddNum).padStart(2, "0");

        // Update the visible value to the corrected date (e.g. 12-31)
        setLocalVal(`${mmStr}-${ddStr}`);

        onDateSelect?.(`${year}-${mmStr}-${ddStr}`);
      } else {
        // Incomplete → propagate empty string so the parent knows
        onDateSelect?.("");
      }
    };

    return (
      <div className="relative w-full">
        <input
          ref={ref}
          type="text"
          value={localVal}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={clsx(className, "pr-10")}
          inputMode="numeric"
        />
        <button
          type="button"
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          onClick={(evt) => {
            evt.preventDefault();
            onClick?.(evt);
          }}
        >
          <CalendarIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }
);
const daysInMonth = (month, year) => new Date(year, month, 0).getDate();

// Helper to get number of days in a month


/* ─────────────────────────────────── main component */
export default function SeasonalPricingEditor({
  value = [],
  onChange,
  errors = {},
  errorKeyPrefix = "",
}) {
  const addPeriod = () =>
    onChange([...value, { start: "", end: "", price: { amount: 0 } }]);

  const update = (idx, patch) =>
    onChange(value.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const sectionErr =
    (errorKeyPrefix && errors?.[`${errorKeyPrefix}.seasons_overlap`]) ||
    // fallback: find-any (keeps working if you can't pass prefix yet)
    (Object.entries(errors).find(([k]) => k.endsWith('.seasons_overlap'))?.[1] ?? null);

  return (
    <div className="mt-6">
      <h4 className="font-semibold mb-2">
        Seasonal Pricing <Required />
      </h4>

      <div className="space-y-3">
        {value.map((season, i) => (
          <div
            key={i}
            className="flex flex-wrap gap-3 items-start text-sm"
          >
            {/* ── start date ───────────────────── */}
            <div className="flex flex-col gap-1">
              <DatePicker
                selected={fromISODate(season.start)}
                onChange={(date) => update(i, { start: toISODate(date) })
                }
                dateFormat="MM-dd"
                placeholderText="Start date"
                customInput={
                  <MMddInput
                    className={clsx(
                      "border rounded px-3 py-2 w-full",
                      seasonErr(errors, i, "start") && "border-red-500"
                    )}
                    onDateSelect={(iso) => update(i, { start: iso })}
                  />
                }
              />
              {seasonErr(errors, i, "start") && (
                <span className="text-xs text-red-600">
                  {seasonErr(errors, i, "start")}
                </span>
              )}
            </div>

            {/* ── end date ─────────────────────── */}
            <div className="flex flex-col gap-1">
              <DatePicker
                selected={season.end ? new Date(season.end) : null}
                onChange={(date) =>
                  update(i, {
                    end: date ? date.toISOString().split("T")[0] : "",
                  })
                }
                dateFormat="MM-dd"
                placeholderText="End date"
                customInput={
                  <MMddInput
                    className={clsx(
                      "border rounded px-3 py-2 w-full",
                      seasonErr(errors, i, "end") && "border-red-500"
                    )}
                    onDateSelect={(iso) => update(i, { end: iso })}
                  />
                }
              />
              {seasonErr(errors, i, "end") && (
                <span className="text-xs text-red-600">
                  {seasonErr(errors, i, "end")}
                </span>
              )}
            </div>

            {/* ── price ─────────────────────────── */}
            <div className="flex flex-col gap-1">
              <input
                type="number"
                min="0"
                step="0.01"
                value={season.price?.amount ?? ""}
                onChange={(e) =>
                  update(i, {
                    price: { amount: parseFloat(e.target.value) || 0 },
                  })
                }
                placeholder="Price"
                className={clsx(
                  "w-28 border rounded px-2 py-1",
                  seasonErr(errors, i, "price") && "border-red-500"
                )}
              />
              {seasonErr(errors, i, "price") && (
                <span className="text-xs text-red-600">
                  {seasonErr(errors, i, "price")}
                </span>
              )}
            </div>

            {/* ── delete row ────────────────────── */}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-red-500 text-lg leading-none mt-2"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
      {sectionErr && (
        <div className="mt-2 text-xs text-red-600">{sectionErr}</div>
      )}
      <button
        type="button"
        onClick={addPeriod}
        className="mt-2 text-blue-600 text-xs hover:underline"
      >
        + Add price period
      </button>
    </div>
  );
}
